// src/pages/TripPlanner/MapVisualization.tsx
import React, { useEffect, useRef, useState } from 'react';
import { Box, Typography, Paper, List, ListItem, ListItemText, Chip } from '@mui/material';
import mapboxgl from 'mapbox-gl';
import 'mapbox-gl/dist/mapbox-gl.css';
import { useTripPlanner } from './TripPlannerContext';

const MapVisualization: React.FC = () => {
  const { tripPlan } = useTripPlanner();
  const mapContainer = useRef<HTMLDivElement>(null);
  const map = useRef<mapboxgl.Map | null>(null);
  const [selectedItem, setSelectedItem] = useState<'accommodation' | 'activity' | null>(null);
  const [selectedId, setSelectedId] = useState<string | null>(null);

  // Initialize map when component mounts
  useEffect(() => {
    if (map.current) return; // Initialize map only once
    
    // Disable map if no valid token to prevent errors
    if (!process.env.REACT_APP_MAPBOX_ACCESS_TOKEN) {
      console.warn('Mapbox token not found. Map features disabled.');
      return;
    }
    
    if (mapContainer.current) {
      try {
        // Set token here
        mapboxgl.accessToken = process.env.REACT_APP_MAPBOX_ACCESS_TOKEN || '';
        
        map.current = new mapboxgl.Map({
          container: mapContainer.current,
          style: 'mapbox://styles/mapbox/streets-v11',
          center: tripPlan.destination?.coordinates || [0, 20],
          zoom: tripPlan.destination ? 12 : 2
        });
        
        map.current.addControl(new mapboxgl.NavigationControl(), 'top-right');
        
        map.current.on('error', (e) => {
          console.error('Mapbox error:', e);
        });
      } catch (error) {
        console.error('Error initializing map:', error);
      }
    }
  }, [tripPlan.destination]);

  // Update map when trip plan changes
  useEffect(() => {
    if (!map.current) return;
    
    // Clear existing markers
    const markers = document.getElementsByClassName('mapboxgl-marker');
    while(markers[0]) {
      markers[0].remove();
    }
    
    // If we have a destination, add a marker and center the map
    if (tripPlan.destination) {
      // Create a destination marker
      const destinationEl = document.createElement('div');
      destinationEl.className = 'destination-marker';
      destinationEl.style.width = '20px';
      destinationEl.style.height = '20px';
      destinationEl.style.borderRadius = '50%';
      destinationEl.style.backgroundColor = '#FFC107';
      destinationEl.style.border = '3px solid white';
      
      new mapboxgl.Marker(destinationEl)
        .setLngLat(tripPlan.destination.coordinates)
        .setPopup(new mapboxgl.Popup({ offset: 25 }).setHTML(
          `<h3>${tripPlan.destination.name}</h3><p>${tripPlan.destination.country}</p>`
        ))
        .addTo(map.current);
      
      // Center the map on destination with appropriate zoom level
      map.current.flyTo({
        center: tripPlan.destination.coordinates,
        zoom: 12,
        essential: true
      });
    }
    
    // Add markers for accommodations
    tripPlan.accommodations.forEach((accommodation) => {
      const el = document.createElement('div');
      el.className = 'accommodation-marker';
      el.style.width = '15px';
      el.style.height = '15px';
      el.style.borderRadius = '50%';
      el.style.backgroundColor = '#FF5722';
      el.style.border = '2px solid white';
      
      // Highlight selected accommodation
      if (selectedItem === 'accommodation' && selectedId === accommodation.id) {
        el.style.backgroundColor = '#FF9800';
        el.style.width = '20px';
        el.style.height = '20px';
        el.style.border = '3px solid white';
      }
      
      new mapboxgl.Marker(el)
        .setLngLat(accommodation.coordinates)
        .setPopup(new mapboxgl.Popup({ offset: 25 }).setHTML(
          `<h3>${accommodation.name}</h3><p>${accommodation.address}</p><p>Rating: ${accommodation.rating}/5</p>`
        ))
        .addTo(map.current!);
    });
    
    // Add markers for activities
    tripPlan.activities.forEach((activity) => {
      const el = document.createElement('div');
      el.className = 'activity-marker';
      el.style.width = '15px';
      el.style.height = '15px';
      el.style.borderRadius = '50%';
      el.style.backgroundColor = '#4CAF50';
      el.style.border = '2px solid white';
      
      // Highlight selected activity
      if (selectedItem === 'activity' && selectedId === activity.id) {
        el.style.backgroundColor = '#8BC34A';
        el.style.width = '20px';
        el.style.height = '20px';
        el.style.border = '3px solid white';
      }
      
      new mapboxgl.Marker(el)
        .setLngLat(activity.coordinates)
        .setPopup(new mapboxgl.Popup({ offset: 25 }).setHTML(
          `<h3>${activity.title}</h3><p>${activity.description}</p><p>${activity.date.toLocaleDateString()}</p>`
        ))
        .addTo(map.current!);
    });
    
    // If there are both accommodations and activities, fit bounds to show all markers
    if (tripPlan.accommodations.length > 0 || tripPlan.activities.length > 0) {
      const bounds = new mapboxgl.LngLatBounds();
      
      // Extend bounds with destination
      if (tripPlan.destination) {
        bounds.extend(tripPlan.destination.coordinates);
      }
      
      // Extend bounds with accommodations
      tripPlan.accommodations.forEach(accommodation => {
        bounds.extend(accommodation.coordinates);
      });
      
      // Extend bounds with activities
      tripPlan.activities.forEach(activity => {
        bounds.extend(activity.coordinates);
      });
      
      // Only fit bounds if we have any points
      if (!bounds.isEmpty()) {
        map.current.fitBounds(bounds, {
          padding: 70,
          maxZoom: 15
        });
      }
    }
  }, [tripPlan, selectedItem, selectedId]);

  const highlightItem = (type: 'accommodation' | 'activity', id: string) => {
    setSelectedItem(type);
    setSelectedId(id);
  };

  const clearHighlight = () => {
    setSelectedItem(null);
    setSelectedId(null);
  };

  return (
    <Box sx={{ height: '100%', display: 'flex', flexDirection: 'column' }}>
      <Box 
        ref={mapContainer} 
        sx={{ 
          flexGrow: 1,
          minHeight: '300px',
        }} 
      />
      
      <Paper sx={{ p: 2, maxHeight: '40%', overflow: 'auto', mt: 2 }}>
        <Typography variant="h6" gutterBottom>
          Trip Locations
        </Typography>
        
        {tripPlan.destination && (
          <Box sx={{ mb: 2 }}>
            <Chip 
              label="Destination" 
              color="warning" 
              size="small" 
              sx={{ mb: 1 }} 
            />
            <Paper 
              variant="outlined" 
              sx={{ p: 1, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}
            >
              <Typography variant="subtitle1">
                {tripPlan.destination.name}, {tripPlan.destination.country}
              </Typography>
            </Paper>
          </Box>
        )}
        
        {tripPlan.accommodations.length > 0 && (
          <Box sx={{ mb: 2 }}>
            <Chip 
              label="Accommodations" 
              color="error" 
              size="small" 
              sx={{ mb: 1 }} 
            />
            <List dense disablePadding>
              {tripPlan.accommodations.map((accommodation) => (
                <ListItem 
                  key={accommodation.id} 
                  component="button"
                  onMouseEnter={() => highlightItem('accommodation', accommodation.id)}
                  onMouseLeave={clearHighlight}
                  sx={{ 
                    bgcolor: selectedItem === 'accommodation' && selectedId === accommodation.id 
                      ? 'action.selected' 
                      : 'transparent',
                    borderRadius: 1
                  }}
                >
                  <ListItemText 
                    primary={accommodation.name} 
                    secondary={accommodation.address} 
                  />
                </ListItem>
              ))}
            </List>
          </Box>
        )}
        
        {tripPlan.activities.length > 0 && (
          <Box>
            <Chip 
              label="Activities" 
              color="success" 
              size="small" 
              sx={{ mb: 1 }} 
            />
            <List dense disablePadding>
              {tripPlan.activities.map((activity) => (
                <ListItem 
                  key={activity.id} 
                  component="button"
                  onMouseEnter={() => highlightItem('activity', activity.id)}
                  onMouseLeave={clearHighlight}
                  sx={{ 
                    bgcolor: selectedItem === 'activity' && selectedId === activity.id 
                      ? 'action.selected' 
                      : 'transparent',
                    borderRadius: 1
                  }}
                >
                  <ListItemText 
                    primary={activity.title} 
                    secondary={`${activity.date.toLocaleDateString()} • ${activity.location}`} 
                  />
                </ListItem>
              ))}
            </List>
          </Box>
        )}
        
        {!tripPlan.destination && tripPlan.accommodations.length === 0 && tripPlan.activities.length === 0 && (
          <Typography variant="body2" color="text.secondary" sx={{ textAlign: 'center', py: 2 }}>
            No locations added to your trip yet.
          </Typography>
        )}
      </Paper>
    </Box>
  );
};

export default MapVisualization;